import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { api, getToken, setToken, setUnauthorizedHandler } from './api';
import type { Session, AuthUser, AuthTenant, AuthAdmin, StaffRole } from '@shared/types';

interface AuthState {
  session: Session | null;
  loading: boolean;
  staff: AuthUser | null;
  tenant: AuthTenant | null;
  admin: AuthAdmin | null;
  currency: string;
  signInStaff: (email: string, password: string) => Promise<void>;
  signInAdmin: (email: string, password: string) => Promise<void>;
  requestTenantCode: (phone: string) => Promise<{ smsConfigured: boolean }>;
  verifyTenantCode: (phone: string, code: string) => Promise<void>;
  bootstrap: (payload: {
    org_name: string; full_name: string; email: string; password: string; currency: string;
  }) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
  can: (minRole: StaffRole) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const ROLE_RANK: Record<StaffRole, number> = { viewer: 0, staff: 1, manager: 2, owner: 3 };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!getToken()) {
      setSession(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get<{ session: Session | null }>('/auth/me');
      setSession(res.session);
    } catch {
      // An unreachable server shouldn't wipe a valid token — just show signed out.
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setSession(null));
    void load();
  }, [load]);

  const signInStaff = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ token: string; session: Session }>('/auth/staff/login', { email, password });
    setToken(res.token);
    setSession(res.session);
  }, []);

  /*
   * The same token store as everybody else, deliberately. One session at a
   * time per browser: signing into the console signs you out of a manager
   * account, which is the right way round — holding both at once is how
   * somebody ends up acting on a customer's data believing they are the
   * customer.
   */
  const signInAdmin = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ token: string; session: Session }>('/admin/login', { email, password });
    setToken(res.token);
    setSession(res.session);
  }, []);

  const requestTenantCode = useCallback(async (phone: string) => {
    return api.post<{ sent: boolean; smsConfigured: boolean }>('/auth/tenant/request-code', { phone });
  }, []);

  const verifyTenantCode = useCallback(async (phone: string, code: string) => {
    const res = await api.post<{ token: string; session: Session }>('/auth/tenant/verify-code', { phone, code });
    setToken(res.token);
    setSession(res.session);
  }, []);

  const bootstrap = useCallback(async (payload: {
    org_name: string; full_name: string; email: string; password: string; currency: string;
  }) => {
    const res = await api.post<{ token: string; session: Session }>('/auth/bootstrap', payload);
    setToken(res.token);
    setSession(res.session);
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setSession(null);
  }, []);

  const value = useMemo<AuthState>(() => {
    const staff = session?.kind === 'staff' ? session : null;
    const tenant = session?.kind === 'tenant' ? session : null;
    const admin = session?.kind === 'admin' ? session : null;
    return {
      session,
      loading,
      staff,
      tenant,
      admin,
      // An admin belongs to no organisation, so there is no currency to read.
      currency: staff?.org.currency ?? tenant?.org.currency ?? 'USD',
      signInStaff,
      signInAdmin,
      requestTenantCode,
      verifyTenantCode,
      bootstrap,
      signOut,
      refresh: load,
      can: (minRole) => !!staff && ROLE_RANK[staff.role] >= ROLE_RANK[minRole],
    };
  }, [session, loading, signInStaff, signInAdmin, requestTenantCode, verifyTenantCode,
    bootstrap, signOut, load]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
